import React, { useCallback } from 'react'
import { Box, Grid, Divider } from '@mui/material'
import { useIntl } from 'react-intl'

import { TextInput } from '../text-input'
import { DateInput } from '../date-input'
import { SelectInput } from '../select-input'
import { NumberInput } from '../number-input'
import { RatingInput } from '../rating-input'
import { ListInput } from '../list-input'

/**
 * @typedef {object} GridConfig
 * @property {number} [xs] - Ancho en pantallas extra pequeñas (0-600px).
 * @property {number} [sm] - Ancho en pantallas pequeñas (600-960px).
 * @property {number} [md] - Ancho en pantallas medianas (960-1280px).
 * @property {number} [lg] - Ancho en pantallas grandes (1280-1920px).
 * @property {number} [xl] - Ancho en pantallas extra grandes (>1920px).
 * @property {boolean} [item] - Si es `true`, el elemento es un item del Grid.
 */

/**
 * @typedef {object} FieldEntry
 * @property {string} ref - Referencia al campo en el `fieldList`.
 * @property {GridConfig} conf - Configuración del Grid de Material-UI para el campo.
 */

/**
 * @typedef {object} FieldDefinition
 * @property {function(object): object} fromData - Función que recibe los datos del formulario y devuelve props dinámicas para el campo.
 * @property {string} name - El nombre del campo.
 * @property {'date' | 'select' | 'text_area' | 'number' | 'rating' | 'list' | 'text'} [componentType] - El tipo de componente a renderizar.
 * @property {React.ComponentType} [component] - Componente personalizado a usar en lugar del componente por defecto.
 * @property {boolean} [hidden] - Si es `true`, el campo no se renderiza.
 * @property {string} [text_invalid] - Mensaje de error que se muestra cuando el campo tiene un error de validación.
 */

/**
 * @typedef {object} BlockDefinition
 * @property {Array<FieldEntry[] | []>} items - Array de filas, donde cada fila es un array de entradas de campo o un array vacío para un divisor.
 * @property {function({ data: object, combos: object, intl: object }): React.ReactNode} [prepend] - Función que devuelve contenido a renderizar antes de los items del bloque.
 * @property {function({ data: object, combos: object, intl: object }): React.ReactNode} [append] - Función que devuelve contenido a renderizar después de los items del bloque.
 */

/**
 * @typedef {object} FormPanelProps
 * @property {object} data - Los datos del formulario que se pasan a las funciones `fromData` de cada campo.
 * @property {object} [combos] - Datos de combos/selects disponibles para los campos, pasados a las funciones `prepend` y `append`.
 * @property {BlockDefinition} block - Definición del bloque de formulario con su estructura y contenido.
 * @property {Object.<string, FieldDefinition>} fieldList - Objeto que mapea referencias de campo a sus definiciones.
 * @property {Object.<string, boolean>} [errors={}] - Objeto que indica qué campos tienen errores de validación.
 * @property {boolean} [showErrors] - Si es `true`, los errores de validación se muestran visualmente en los campos.
 * @property {React.ReactNode} [extraContent] - Contenido adicional que se renderiza al final del bloque.
 */

/**
 * Un componente de panel de formulario dinámico que renderiza campos basándose en una configuración declarativa.
 * 
 * Este componente proporciona una forma flexible y escalable de construir formularios complejos mediante
 * una configuración basada en objetos. Las características principales incluyen:
 * 
 * - **Renderizado dinámico**: Los campos se generan a partir de una configuración declarativa
 * - **Componentes por defecto**: Mapeo automático de tipos a componentes (date → DateInput, select → SelectInput, etc.)
 * - **Componentes personalizados**: Soporte para componentes custom por campo
 * - **Layout responsive**: Utiliza Material-UI Grid para layouts adaptativos
 * - **Validación visual**: Muestra errores de validación con mensajes personalizados
 * - **Campos condicionales**: Soporte para ocultar campos dinámicamente mediante la prop `hidden`
 * - **Contenido adicional**: Permite insertar contenido antes (prepend) y después (append) de los campos
 * - **Divisores**: Soporta filas vacías que se renderizan como divisores visuales
 * 
 * El componente utiliza el patrón de configuración sobre código, permitiendo definir formularios
 * complejos de manera declarativa y reutilizable.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [formData, setFormData] = useState({ nombre: '', edad: 0, fecha_nacimiento: null });
 *   const [errors, setErrors] = useState({});
 * 
 *   const fieldList = {
 *     nombre: {
 *       fromData: (data) => ({ value: data.nombre, onChange: handleChange }),
 *       name: 'nombre',
 *       componentType: 'text',
 *       text_invalid: 'El nombre es requerido',
 *     },
 *     edad: {
 *       fromData: (data) => ({ value: data.edad, onChange: handleChange }),
 *       name: 'edad',
 *       componentType: 'number',
 *       text_invalid: 'La edad debe ser mayor a 0',
 *     },
 *     fecha_nacimiento: {
 *       fromData: (data) => ({ value: data.fecha_nacimiento, onChange: handleChange }),
 *       name: 'fecha_nacimiento',
 *       componentType: 'date',
 *     },
 *   };
 * 
 *   const block = {
 *     items: [
 *       [
 *         { ref: 'nombre', conf: { xs: 12, md: 6, item: true } },
 *         { ref: 'edad', conf: { xs: 12, md: 6, item: true } },
 *       ],
 *       [], // Divisor
 *       [
 *         { ref: 'fecha_nacimiento', conf: { xs: 12, item: true } },
 *       ],
 *     ],
 *     prepend: ({ data }) => <Typography variant="h6">Datos Personales</Typography>,
 *   };
 * 
 *   return (
 *     <FormPanel
 *       data={formData}
 *       block={block}
 *       fieldList={fieldList}
 *       errors={errors}
 *       showErrors={true}
 *     />
 *   );
 * };
 * 
 * @param {FormPanelProps} props - Las props del componente FormPanel.
 */

const Component = (props) => {
  const { component, defaultComponent, componentType, error, ...entryData } = props
  const ComponentClass = component || defaultComponent(componentType)
  return (
    <ComponentClass
      {...{
        ...entryData,
        error: error,
        helperText: error && entryData.text_invalid,
      }}
    />
  )
}

const FormPanelBlockItem = ({ defaultComponent, data, errors, showErrors, fieldList, row, i }) => {
  return (
    <Grid container spacing={3} key={i} style={{ marginTop: 0, marginBottom: 0 }}>
      {row.length ? (
        row.map((entry) => {
          const { fromData, ...staticElement } = fieldList[entry.ref]
          const element = {
            ...staticElement,
            ...fromData(data),
          }
          if (element?.hidden) return null
          const { component, componentType, ...entryData } = element || {}
          const error = errors[entryData.name] && showErrors
          return (
            <Grid {...entry.conf} key={entry.ref}>
              <Component {...{ component, defaultComponent, componentType, error, ...entryData }} />
            </Grid>
          )
        })
      ) : (
        <Divider light style={{ width: '100%' }} />
      )}
    </Grid>
  )
}

const FormPanelBlock = ({
  defaultComponent,
  block,
  data,
  errors,
  showErrors,
  combos,
  fieldList,
  extraContent,
}) => {
  const intl = useIntl()
  return (
    <Box display="block" style={{ width: '100%' }}>
      {block.prepend ? block.prepend({ data, combos, intl }) : null}
      {block.items.map((row, i) => {
        return (
          <FormPanelBlockItem
            key={i}
            {...{ defaultComponent, data, errors, showErrors, fieldList, row, i }}
          />
        )
      })}
      {block.append ? block.append({ data, combos, intl }) : null}
      {extraContent}
    </Box>
  )
}

export const FormPanel = ({
  data,
  combos,
  block,
  fieldList,
  errors = {},
  showErrors,
  extraContent,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const defaultComponent = useCallback(
    (type) => {
      switch (type) {
        case 'date':
          return <DateInput {...{ formatDate, formatMessage }} />
        case 'select':
          return SelectInput
        case 'text_area':
          return TextInput
        case 'number':
          return NumberInput
        case 'rating':
          return RatingInput
        case 'list':
          return ListInput
        default:
          return TextInput
      }
    },
    [formatDate, formatMessage]
  )

  return (
    <FormPanelBlock
      {...{
        defaultComponent,
        block,
        data,
        errors,
        showErrors,
        combos,
        fieldList,
        extraContent,
      }}
    />
  )
}

export default FormPanel
